import type { FC } from 'react';

import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { FittingNav } from '@/features/storefront/layouts/chrome/FittingNav';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/** The house's navigation voice, worn by the menu and by its stand-in alike. */
const SERRA_NAV = 'gap-6 text-[11px] font-medium uppercase tracking-[0.12em]';

/**
 * A fashion house's masthead: an announcement rail in reverse, then the
 * wordmark centred between the navigation and the shopper's own links.
 *
 * The rail is the theme's text colour carrying its background colour, so it
 * inverts correctly whatever palette the merchant lands on - a dark theme gets
 * a light rail rather than a black bar that vanishes into the page.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);
  return (
    <header
      className={cn('border-b', header.sticky && !preview && 'sticky top-0 z-40')}
      style={{
        borderColor: 'var(--storefront-surface)',
        backgroundColor: 'var(--storefront-background)',
      }}
    >
      {/* This family is drawn around a rail, so with no banners written it
          keeps carrying the shop's own line rather than closing up. */}
      <HeaderBannerRail header={header} fallback={description} preview={preview} />
      {/* A row on a phone - menu button, wordmark, corner - where the buttons
          keep their room and the wordmark gives way, truncating a long shop
          name rather than running over the menu button and the search: three
          equal-sided columns there would hand a long name the whole row. The
          centred three columns from `lg`, as the family is drawn. */}
      <StorefrontContainer className="flex items-center gap-3 pb-5 pt-6 sm:gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr]">
        {/* Always rendered, even when it holds nothing: this is the grid's
            first 1fr column, and dropping it would slide the wordmark out of
            the centre it is designed around. */}
        <div className="flex shrink-0 items-center lg:min-w-0" data-serra-start="">
          {/* Serra keeps its nav until lg - the wordmark needs the room - so
              its trigger stays out later than the other families'. */}
          <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryFallback />
          {items.length > 0 ? (
            /* As many items as fit, the rest under "More": the column is
               a third of the page, and a merchant's menu that ran past it
               would run under the wordmark. */
            <div className="hidden min-w-0 flex-1 lg:flex">
              <FittingNav
                items={items}
                label={menu?.name}
                preview={preview}
                navClassName={cn('items-center whitespace-nowrap', SERRA_NAV)}
                moreClassName="hover:opacity-60"
                className="justify-start"
                renderItem={(item) => (
                  <MenuLink
                    key={`${item.label}:${item.url}`}
                    item={item}
                    preview={preview}
                    className="hover:opacity-60"
                  />
                )}
              />
            </div>
          ) : preview ? null : (
            /* No menu built yet. A masthead with nothing to its left reads as
               a broken header, so the shop's own top-level categories stand in
               - the way into the catalogue is what this strip is for, and it
               is capped to keep it a strip. Skipped in preview, where no
               storefront host is resolved and the query would ask about the
               wrong shop. */
            <HeaderCategoryLinks
              className={cn('hidden items-center whitespace-nowrap lg:flex', SERRA_NAV)}
              linkClassName="hover:opacity-60"
              header={header}
            />
          )}
        </div>
        {/* The rest of the phone's row, centred; the logo's own column from
            `lg`. `StoreLogo` truncates the name within whatever this leaves. */}
        <div className="flex min-w-0 flex-1 justify-center lg:justify-self-center" data-serra-logo="">
          <StoreLogo
            storeName={storeName}
            logoUrl={logoUrl}
            logoWidthPx={header.logo_width_px}
            showStoreName={header.show_store_name}
            preview={preview}
            className="gap-3"
            nameClassName="whitespace-nowrap text-2xl uppercase tracking-[0.14em] sm:text-3xl"
            nameStyle={{ fontFamily: 'var(--storefront-heading-font)' }}
          />
        </div>
        <div className="flex shrink-0 items-center justify-end gap-6" data-serra-end="">
          {endSlot}
        </div>
      </StorefrontContainer>
    </header>
  );
};
